import React, { useState } from 'react';
import { useExpenseParticipants } from '../hooks/queries/useExpenseParticipants';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/formatters';
import { CurrencyCode } from '../constants/currencies';
import { ArrowRight } from 'lucide-react';
import { icons } from '../assets/icons';

interface GroupedExpense {
  date: string;
  expenses: any[];
}

interface ExpensesListProps {
  groupCurrency: CurrencyCode;
  memberNameMap: Map<string, string>;
  currentUserMemberId: string;
  groupedExpenses: GroupedExpense[];
  rawExpenses: any[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

// 개별 카드 컴포넌트 생성
const ExpenseCard: React.FC<{ expense: any, groupCurrency: CurrencyCode, memberNameMap: Map<string, string>, currentUserMemberId: string }> = ({ expense, groupCurrency, memberNameMap, currentUserMemberId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const isCurrentUserPayer = expense.payer.id === currentUserMemberId;
  const { data: participants, isLoading: participantsLoading } = useExpenseParticipants(expense.id, isExpanded);

  return (
    <div 
      className='bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 cursor-pointer'
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="flex justify-between mb-1">
        <h3 className="text-gray-800 font-bold flex items-center">
          {expense.title || '지출'}
        </h3>
        {expense.currency !== groupCurrency && <span className="text-gray-400 font-mono-number">{formatCurrency(expense.amount_in_currency, expense.currency as CurrencyCode )}</span>}
      </div>
      <div className="flex justify-between items-center text-base">
        <div className="flex-1 flex items-center">
          {
            expense.is_transfer ? (
              <div className='w-full flex items-center gap-0.5 text-gray-500'>
                <span>{expense.payer.user_name}</span>
                <ArrowRight className='w-4 h-4' />
                <span>{memberNameMap.get(expense.transfer_to)}</span>
              </div>
            ) : (
              <span className='text-gray-500'>{expense.payer.user_name}{isCurrentUserPayer && <span>(나)</span>}</span>
            )
          }
        </div>
        <span className="text-lg text-gray-800 font-bold font-mono-number">{formatCurrency(expense.amount, groupCurrency)}</span>
      </div>

      {isExpanded && (
        <div className="mt-2 border-t-1 border-white pt-2">
          <div className="flex justify-end items-end mb-1">
            <button className="flex items-center gap-0.5 py-1 px-1.5 rounded-lg text-sm font-bold bg-white text-gray-600 cursor-pointer" onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              navigate(`/groups/${expense.group_id}/edit-expense/${expense.id}`);
            }}><img src={icons.pencil} className="w-4 h-4"/>수정</button>
          </div>
          {
            !expense.is_transfer && (
              <ul className="w-full flex flex-col items-center text-base text-gray-600 mt-1 space-y-1">
              {
                participantsLoading ? (
                  <div className=" w-4 h-4 border-2 mb-2 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  participants?.sort((a: any) => a.member_id === currentUserMemberId ? -1 : 1).map((participant: any) => {
                    return (
                    <li key={participant.id} className="flex justify-between w-full">
                      <span className="flex items-center text-gray-800 font-bold text-sm">
                        {memberNameMap.get(participant.member_id) || "-"}
                        {participant.member_id === currentUserMemberId && <span>(나)</span>}
                      </span>
                      <span className="text-gray-400 text-sm font-mono-number">{formatCurrency(participant.amount, groupCurrency)}</span>
                    </li>
                  )
                })
              )}
            </ul>
            )
          }
        </div>
      )}
    </div>
  );
};

const ExpensesList: React.FC<ExpensesListProps> = ({ 
  groupCurrency, 
  memberNameMap, 
  currentUserMemberId,
  groupedExpenses,
  rawExpenses,
  isLoading,
  isError
}) => {
  if (isLoading || isError || !rawExpenses) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="w-8 h-8 border-4 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupedExpenses.length === 0 ? (
        <div className="flex flex-col justify-center items-center my-8 h-full">
          <p className="text-gray-500">등록된 지출 내역이 없습니다.</p>
          <p className="text-gray-500">하단의 + 버튼을 눌러 지출 내역을 등록해주세요.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedExpenses.map((expenseGroup) => (
            <div key={expenseGroup.date} className="space-y-2 last:mb-32">
              <div className="text-gray-800 text-lg font-bold">{expenseGroup.date}</div>
              {
                expenseGroup.expenses.map((expense: any) => (
                  <ExpenseCard key={expense.id} expense={expense} groupCurrency={groupCurrency} memberNameMap={memberNameMap} currentUserMemberId={currentUserMemberId} />
                ))
              }
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpensesList; 